import React from 'react';
import { classNames, If, RootClass } from 'react-extras';
import { Grid, GridRow, GridCol } from 'components/UI/Grid';
import { PrismicLink } from 'components/PrismicLink';
import { LrOverlay } from 'components/LANDR/LrOverlay';
import { LrLogo } from 'components/Icons/LrLogo';
import { homepageLink } from 'helpers';
import { PrismicExactImage } from 'components/Prismic';
import { MainNavigationProps, MainNavigationState } from './types';
import { LogoKebab } from './LogoKebab';
import { LogoClose } from './LogoClose';
import SignUpOrGoToApp from './SignUpOrGoToApp';
import LogInLogOut from './LogInLogOut';
import './MainNavigation.scss';

class MainNavigation extends React.Component<MainNavigationProps, MainNavigationState> {
    navElement: HTMLDivElement;
    navMobileElement: HTMLDivElement;

    constructor(props: MainNavigationProps) {
        super(props);
        this.state = {
            isOpened: false,
            isFixed: false,
            isAnchored: false,
        };
    }

    componentDidMount() {
        window.addEventListener('scroll', this.handleScroll);
    }

    componentWillUnmount() {
        window.removeEventListener('scroll', this.handleScroll);
    }

    handleScroll = () => {
        const scrollOffset = this.navElement.clientHeight || this.navMobileElement.clientHeight;
        const scrollPos = document.documentElement.scrollTop;
        const shouldFix = scrollPos > scrollOffset && !this.state.isFixed;
        const shouldUnfix = scrollPos <= scrollOffset && this.state.isFixed;
        const shouldReset = scrollPos === 0;

        if (shouldReset) {
            return this.resetMenu();
        }
        if (shouldFix) {
            return this.fixMenu();
        }
        if (shouldUnfix) {
            return this.unfixMenu();
        }
    };

    fixMenu = () => this.setState({ isFixed: true, isAnchored: false });

    unfixMenu = () => this.setState({ isFixed: true, isAnchored: true });

    resetMenu = () => this.setState({ isFixed: false, isAnchored: false });

    toggleMobileMenu = () => this.setState({ isOpened: !this.state.isOpened });

    render() {
        const { menu_items, login_label, signup_label, go_to_app_label, logout_label } = this.props.mainNavigationData;

        const { isOpened, isFixed, isAnchored } = this.state;

        return (
            <div
                className={classNames('MainNavigation', {
                    'is-fixed': isFixed,
                    'is-anchored': isAnchored,
                    'is-opened': isOpened,
                })}
                ref={ref => (this.navElement = ref || ({} as HTMLDivElement))}
            >
                <If condition={isOpened}>
                    <RootClass add="is-mainNavigation-open" />
                </If>

                <Grid className="MainNavigation-content">
                    <GridRow horizontalAlign={'h-between'} verticalAlign={'v-center'} className="Header-content">
                        <GridCol colSize={this.props.headerType !== 'PartnerHeader' ? `2` : `6`} offsetSize={`0`}>
                            <PrismicLink
                                link={homepageLink(this.props.pageContext.lang)}
                                className="MainNavigation-logo"
                            >
                                <LrLogo />
                            </PrismicLink>
                            {this.props.headerType === 'PartnerHeader' && this.props.partnerLogo !== null && (
                                <>
                                    <div className="MainNavigation-spacer">&times;</div>
                                    {this.props.partnerUrl !== null ? (
                                        <PrismicLink
                                            link={this.props.partnerUrl}
                                            className="MainNavigation-partnerLogo"
                                        >
                                            <PrismicExactImage image={this.props.partnerLogo} width="150px" />
                                        </PrismicLink>
                                    ) : (
                                        <div className="MainNavigation-partnerLogo noCta">
                                            <PrismicExactImage image={this.props.partnerLogo} width="150px" />
                                        </div>
                                    )}
                                </>
                            )}
                        </GridCol>
                        <button className="MainNavigation-toggle" onClick={this.toggleMobileMenu}>
                            <LogoClose />
                        </button>
                        <GridCol
                            colSize={this.props.headerType !== 'PartnerHeader' ? `10` : `6`}
                            mobileColSize={'12'}
                            tabletColSize={'12'}
                            offsetSize={'0'}
                        >
                            {this.props.headerType !== 'OnlyLogo' && (
                                <ul className="MainNavigation-items" onClick={this.toggleMobileMenu}>
                                    {this.props.headerType !== 'PartnerHeader' && (
                                        <>
                                            {menu_items.map((item, index) => (
                                                <li key={index} className="MainNavigation-item">
                                                    <PrismicLink
                                                        label={item.label}
                                                        link={item.link}
                                                        className="MainNavigation-itemButton"
                                                    />
                                                </li>
                                            ))}
                                            <li className="MainNavigation-item MainNavigation-item--secondary">
                                                <span className="Sign-up--mobileBtn">
                                                    <SignUpOrGoToApp
                                                        label={signup_label}
                                                        goToAppLabel={go_to_app_label}
                                                    />
                                                </span>
                                                <LogInLogOut
                                                    loginLabel={login_label}
                                                    logoutLabel={logout_label}
                                                    className="MainNavigation-itemButton"
                                                />
                                            </li>
                                        </>
                                    )}
                                    <li
                                        className={`MainNavigation-item MainNavigation-item--secondary Sign-up ${
                                            this.props.headerType === 'PartnerHeader' ? 'SignUpPartner' : ''
                                        }`}
                                    >
                                        <span className="u-hidden-lg-down">
                                            <SignUpOrGoToApp label={signup_label} goToAppLabel={go_to_app_label} />
                                        </span>
                                    </li>
                                </ul>
                            )}
                        </GridCol>
                    </GridRow>
                </Grid>

                <div
                    className="MainNavigation-mobileContent"
                    ref={ref => (this.navMobileElement = ref || ({} as HTMLDivElement))}
                >
                    <Grid>
                        <GridRow horizontalAlign={'h-between'} verticalAlign={'v-center'} className="Header-content">
                            <GridCol
                                colSize={this.props.headerType === 'PartnerHeader' ? '8' : '4'}
                                mobileColSize={this.props.headerType === 'PartnerHeader' ? '10' : '8'}
                                offsetSize={`0`}
                            >
                                <PrismicLink
                                    link={homepageLink(this.props.pageContext.lang)}
                                    className="MainNavigation-logo"
                                >
                                    <LrLogo />
                                </PrismicLink>
                                {this.props.headerType === 'PartnerHeader' && this.props.partnerLogo !== null && (
                                    <>
                                        <div className="MainNavigation-spacer">&times;</div>
                                        {this.props.partnerUrl !== null ? (
                                            <PrismicLink
                                                link={this.props.partnerUrl}
                                                className="MainNavigation-partnerLogo"
                                            >
                                                <PrismicExactImage image={this.props.partnerLogo} width="120px" />
                                            </PrismicLink>
                                        ) : (
                                            <div className="MainNavigation-partnerLogo noCta">
                                                <PrismicExactImage image={this.props.partnerLogo} width="120px" />
                                            </div>
                                        )}
                                    </>
                                )}
                            </GridCol>
                            {this.props.headerType !== 'OnlyLogo' && (
                                <GridCol
                                    colSize={this.props.headerType === 'PartnerHeader' ? '4' : '8'}
                                    mobileColSize={this.props.headerType === 'PartnerHeader' ? '2' : '4'}
                                    offsetSize={`0`}
                                >
                                    <ul className="MainNavigation-mobileContent-items">
                                        {/* <li className="MainNavigation-mobileContent-item"> */}
                                        <li
                                            className={`MainNavigation-mobileContent-item ${
                                                this.props.headerType === 'PartnerHeader' ? 'SignUpPartner' : ''
                                            }`}
                                        >
                                            <SignUpOrGoToApp label={signup_label} goToAppLabel={go_to_app_label} />
                                        </li>
                                        {this.props.headerType !== 'PartnerHeader' && (
                                            <li className="MainNavigation-mobileContent-item">
                                                <button onClick={this.toggleMobileMenu}>
                                                    <LogoKebab />
                                                </button>
                                            </li>
                                        )}
                                    </ul>
                                </GridCol>
                            )}
                        </GridRow>
                    </Grid>
                </div>

                <div className="MainNavigation-mobileOverlay" onClick={this.toggleMobileMenu}>
                    <LrOverlay />
                </div>
            </div>
        );
    }
}

export { MainNavigation };
